// Temporary client-side token storage to keep the POS working while the
// backend hasn't yet shipped the cookie-based auth flow promised by the
// security audit (PR #270).
//
// The backend still returns Devise Token Auth headers — access-token /
// client / uid — on every authenticated response, and expects them back on
// every authenticated request. We capture them here and re-send via an axios
// interceptor (installed in authHelper.js).
//
// We use sessionStorage rather than localStorage:
//   - The audit's stated goal of removing tokens from localStorage is preserved.
//   - Tokens clear when the browser session ends — smaller attack surface
//     than localStorage in case of XSS.
//   - The downside (re-login each session) is acceptable for now.
//
// When the backend ships the HttpOnly-cookie flow, delete this module and
// the two `axios.interceptors.*.use(...)` calls in authHelper.js.

const STORAGE_KEY = "wosh.pos.deviseTokens";

const TOKEN_HEADER_NAMES = [
  "access-token",
  "client",
  "uid",
  "expiry",
  "token-type",
];

/**
 * Persist Devise Token Auth headers from a successful auth response.
 * Devise rotates `access-token` on every request, so this should be called
 * from a response interceptor too — not just on login.
 */
export function saveAuthTokens(responseHeaders) {
  if (!responseHeaders) return;
  // Axios lowercases response header names; back-compat with mixed-case.
  const headers = {};
  TOKEN_HEADER_NAMES.forEach((name) => {
    headers[name] = responseHeaders[name] ?? responseHeaders[name.toLowerCase()];
  });
  if (!headers["access-token"]) return; // not an auth response — skip
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(headers));
}

/**
 * Read the currently-stored tokens, or null if none.
 */
export function getAuthTokens() {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Clear stored tokens. Called on logout.
 */
export function clearAuthTokens() {
  sessionStorage.removeItem(STORAGE_KEY);
}
